/** Small drawing helpers shared by the game illustrations (all plain SVG, no filters – cheap to draw on any computer). */
import type { ReactNode } from "react";

export type SceneProps = { u: string };

/** a sky/background with a soft light in the middle */
export function Bg({ u, a, b, glow = 0.6, cx = 50, cy = 45 }: { u: string; a: string; b: string; glow?: number; cx?: number; cy?: number }) {
  return (
    <>
      <defs>
        <linearGradient id={`${u}bg`} x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor={a} />
          <stop offset="1" stopColor={b} />
        </linearGradient>
        <radialGradient id={`${u}gl`} cx={`${cx}%`} cy={`${cy}%`} r="60%">
          <stop offset="0" stopColor="#fff" stopOpacity={glow} />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="640" height="360" fill={`url(#${u}bg)`} />
      <rect width="640" height="360" fill={`url(#${u}gl)`} />
    </>
  );
}

/** thin rays from a point (sunburst) */
export function Rays({ cx = 320, cy = 180, n = 18, len = 520, color = "#fff", opacity = 0.18, spread = 0.055, rot = 0 }: { cx?: number; cy?: number; n?: number; len?: number; color?: string; opacity?: number; spread?: number; rot?: number }) {
  const p: ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2;
    const x1 = cx + Math.cos(a - spread) * len, y1 = cy + Math.sin(a - spread) * len;
    const x2 = cx + Math.cos(a + spread) * len, y2 = cy + Math.sin(a + spread) * len;
    p.push(<polygon key={i} points={`${cx},${cy} ${x1.toFixed(1)},${y1.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`} fill={color} opacity={opacity} />);
  }
  return <g>{p}</g>;
}

/** four-point sparkle */
export function Sparkle({ x, y, s = 12, fill = "#fff", o = 1, rot = 0 }: { x: number; y: number; s?: number; fill?: string; o?: number; rot?: number }) {
  const k = s * 0.22;
  return <path transform={`translate(${x} ${y}) rotate(${rot})`} d={`M0 ${-s} Q${k} ${-k} ${s} 0 Q${k} ${k} 0 ${s} Q${-k} ${k} ${-s} 0 Q${-k} ${-k} 0 ${-s}Z`} fill={fill} opacity={o} />;
}

export function Star5({ x, y, r = 14, fill = "#ffd23f", stroke = "#e08e00", sw = 2, rot = 0 }: { x: number; y: number; r?: number; fill?: string; stroke?: string; sw?: number; rot?: number }) {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (i * Math.PI) / 5 - Math.PI / 2, rr = i % 2 ? r * 0.46 : r;
    pts.push(`${(Math.cos(a) * rr).toFixed(1)},${(Math.sin(a) * rr).toFixed(1)}`);
  }
  return <polygon transform={`translate(${x} ${y}) rotate(${rot})`} points={pts.join(" ")} fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />;
}

/** a gold coin seen from the front */
export function Coin({ x, y, r = 16, rot = 0 }: { x: number; y: number; r?: number; rot?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <circle r={r} cy={r * 0.12} fill="#b86500" />
      <circle r={r} fill="#ffc61a" stroke="#d98200" strokeWidth={r * 0.1} />
      <circle r={r * 0.72} fill="#ffe27a" stroke="#e29a00" strokeWidth={r * 0.07} />
      <Star5 x={0} y={r * 0.03} r={r * 0.46} fill="#f5a300" stroke="none" sw={0} />
      <path d={`M${-r * 0.62} ${-r * 0.32} A${r * 0.7} ${r * 0.7} 0 0 1 ${r * 0.1} ${-r * 0.7}`} fill="none" stroke="#fff" strokeOpacity={0.75} strokeWidth={r * 0.12} strokeLinecap="round" />
    </g>
  );
}

/** a stack of coins seen from the side */
export function CoinStack({ x, y, n = 4, r = 20 }: { x: number; y: number; n?: number; r?: number }) {
  const h = r * 0.34, ry = r * 0.38;
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx={0} cy={r * 0.15} rx={r * 1.1} ry={ry * 0.8} fill="#000" opacity={0.18} />
      {Array.from({ length: n }, (_, i) => {
        const yy = -i * h;
        return (
          <g key={i} transform={`translate(${i % 2 ? 1.5 : -1} ${yy})`}>
            <path d={`M${-r} 0 v${-h} a${r} ${ry} 0 0 1 ${2 * r} 0 v${h} a${r} ${ry} 0 0 1 ${-2 * r} 0Z`} fill="#e08e00" />
            <ellipse cx={0} cy={-h} rx={r} ry={ry} fill="#ffc61a" stroke="#d98200" strokeWidth={1.4} />
            {i === n - 1 && <ellipse cx={0} cy={-h} rx={r * 0.66} ry={ry * 0.62} fill="#ffe27a" />}
          </g>
        );
      })}
    </g>
  );
}

export function Cloud({ x, y, s = 1, o = 0.9, fill = "#fff" }: { x: number; y: number; s?: number; o?: number; fill?: string }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} opacity={o} fill={fill}>
      <ellipse cx={0} cy={10} rx={46} ry={14} />
      <circle cx={-18} cy={0} r={16} />
      <circle cx={6} cy={-8} r={21} />
      <circle cx={28} cy={2} r={14} />
    </g>
  );
}

/** a gem (rhombus with facets) */
export function Gem({ x, y, s = 14, a = "#7ee8ff", b = "#1f9bd6" }: { x: number; y: number; s?: number; a?: string; b?: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <polygon points={`${-s},${-s * 0.2} ${-s * 0.55},${-s * 0.85} ${s * 0.55},${-s * 0.85} ${s},${-s * 0.2} 0,${s}`} fill={b} stroke="#fff" strokeOpacity={0.6} strokeWidth={1.2} strokeLinejoin="round" />
      <polygon points={`${-s},${-s * 0.2} ${-s * 0.55},${-s * 0.85} 0,${-s * 0.2}`} fill={a} />
      <polygon points={`${-s * 0.55},${-s * 0.85} ${s * 0.55},${-s * 0.85} 0,${-s * 0.2}`} fill="#fff" opacity={0.55} />
      <polygon points={`${s * 0.55},${-s * 0.85} ${s},${-s * 0.2} 0,${-s * 0.2}`} fill={a} opacity={0.8} />
    </g>
  );
}

/** a cube drawn in 3D (top, left, right faces) – `pips` 1..6 gives the dice face on the top and sides */
export function Cube({ x, y, s = 34, top = "#ffffff", left = "#e8eefc", right = "#c3d0ee", rot = 0, mark }: { x: number; y: number; s?: number; top?: string; left?: string; right?: string; rot?: number; mark?: (face: "t" | "l" | "r") => ReactNode }) {
  const w = s * 0.87, h = s * 0.5;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <polygon points={`0,${-s} ${w},${-s + h} 0,${-s + 2 * h} ${-w},${-s + h}`} fill={top} />
      <polygon points={`${-w},${-s + h} 0,${-s + 2 * h} 0,${h * 2 + 0} ${-w},${h}`} fill={left} />
      <polygon points={`${w},${-s + h} 0,${-s + 2 * h} 0,${h * 2 + 0} ${w},${h}`} fill={right} />
      <polyline points={`${-w},${-s + h} 0,${-s + 2 * h} ${w},${-s + h}`} fill="none" stroke="#fff" strokeOpacity={0.0} />
      {mark && <>{mark("t")}{mark("l")}{mark("r")}</>}
    </g>
  );
}

export function Pill({ x, y, w, h = 30, fill = "#fff", text, tc = "#14246a", stroke, fs = 18 }: { x: number; y: number; w: number; h?: number; fill?: string; text?: string; tc?: string; stroke?: string; fs?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height={h} rx={h / 2} fill={fill} stroke={stroke} strokeWidth={stroke ? 3 : 0} />
      {text && <text x={w / 2} y={h / 2 + fs * 0.36} textAnchor="middle" fontSize={fs} fontWeight={900} fill={tc} style={{ fontFamily: "var(--display)" }}>{text}</text>}
    </g>
  );
}

/** horizontal speed streaks */
export function Streaks({ items, color = "#fff", o = 0.7 }: { items: [number, number, number][]; color?: string; o?: number }) {
  return (
    <g stroke={color} strokeLinecap="round" opacity={o}>
      {items.map(([x, y, l], i) => <line key={i} x1={x} y1={y} x2={x + l} y2={y} strokeWidth={4 + (i % 3) * 2} />)}
    </g>
  );
}

/** the three layers every scene is made of – the page moves them a little against each other when the mouse is over the picture */
export const L0 = ({ children }: { children: ReactNode }) => <g className="ga-l0">{children}</g>;
export const L1 = ({ children }: { children: ReactNode }) => <g className="ga-l1">{children}</g>;
export const L2 = ({ children }: { children: ReactNode }) => <g className="ga-l2">{children}</g>;
